import { useCallback, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { getFrontendInstance, getCompany } from 'helpers/getCompanyVariables';

// "Hidden Mode" lets a user mask sensitive values (emails, phone numbers, bank
// accounts, …) on screen so they can share or screen-share the portal safely.
//
// The toggle lives in the PageHeader while the values it hides live in
// unrelated screens (AgencyDashboard, MyProfile). Rather than thread the state
// through React context, it is kept in a small localStorage-backed external
// store so every subscribing component re-renders the moment it flips — and the
// preference is persisted per user, surviving reloads and staying in sync
// across open tabs.

const KEY_PREFIX = 'oneill_hidden_mode:';
const MASK = '••••••••';

const listeners = new Set();

const keyFor = userId => `${KEY_PREFIX}${userId || 'anonymous'}`;

function notify() {
  listeners.forEach(listener => listener());
}

function readValue(userId) {
  // In production and for Sunshine, Hidden Mode is always off — sensitive values
  // are never masked there.
  if (getFrontendInstance() === 'production' || getCompany() === 'sunshine') {
    return false;
  }
  try {
    return localStorage.getItem(keyFor(userId)) === 'true';
  } catch {
    return false;
  }
}

function writeValue(userId, value) {
  try {
    localStorage.setItem(keyFor(userId), String(!!value));
  } catch {
    /* ignore quota / unavailable storage — fall through to notify */
  }
  notify();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Keep every open tab for the same user in sync.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (!event.key || event.key.startsWith(KEY_PREFIX)) notify();
  });
}

/**
 * Replace a sensitive value with a fixed mask while Hidden Mode is on. Blank
 * values are returned untouched so empty-field placeholders (e.g. "-") still
 * render normally.
 */
export function maskValue(value, hidden, mask = MASK) {
  if (!hidden) return value;
  const str = value == null ? '' : String(value);
  return str.trim() === '' ? value : mask;
}

/**
 * @returns {{ hidden: boolean, setHidden: (value: boolean) => void, toggleHidden: () => void }}
 */
export default function useHiddenMode() {
  const userId = useSelector(
    state => state.user?.mainZohoId || state.user?.zohoData?.id
  );

  const getSnapshot = useCallback(() => readValue(userId), [userId]);
  const hidden = useSyncExternalStore(subscribe, getSnapshot);

  const setHidden = useCallback(value => writeValue(userId, value), [userId]);
  const toggleHidden = useCallback(
    () => writeValue(userId, !readValue(userId)),
    [userId]
  );

  return { hidden, setHidden, toggleHidden };
}
